import { useNavigate, useParams } from "react-router-dom"
import { useWatches } from "../../../context/watchProvider";
import { useEffect, useState } from "react";
import WatchImages from "./watchImages";
import MobileWatchImages from "./mobileWatchImages";
import PlusIcon from "../../../img/svg/plusIcon";
import ChevronDownIcon from "../../../img/svg/chevronDownIcon";
import { braceletMaterialFields, caseMaterialFields, functionsFields, glassFields, movementFields, stateFields } from "../../../utils/fields";
import AdvertiserModal from "./advertiserModal";
import { useAuth } from "../../../context/authProvider";
import LoadingBalls from "../../core/loadingBalls";
import { calculateRate, formatPhoneNumber } from "../../../utils/functions";
import TemplateStars from "../../core/templateStars";
import Review from "../../core/review";
import VerifiedWatch from "../../../img/svg/verifiedWatch";
import UserIcon from "../../../img/svg/userIcon";

export default function WatchPage({ isMobile }: { isMobile: boolean }) {

    const { slug } = useParams();
    const { getWatchBySlug, addToCart, getCartItems, removeFromCart, cart, cartUpdated, getAdvertiser, getPublicAdvertiserReviews, getWatchViews, countWatchView } = useWatches();
    const navigate = useNavigate();
    const { userId, user } = useAuth();
    const isBlocked = !user || !user?.emailVerified || !user?.termsAccepted?.accepted;

    const [watch, setWatch] = useState<{ [key: string]: any }>({});
    const [details, setDetails] = useState(false);
    const [advertiserModal, setAdvertiserModal] = useState(false);
    const [advertiser, setAdvertiser] = useState<{ [key: string]: any } | null>(null);
    const [reviews, setReviews] = useState<{ rate: number, review: string, createdAt: Date }[] | null>(null);
    const [reviewIdx, setReviewIdx] = useState(0);

    const [loading, setLoading] = useState(true);
    const [cartLoading, setCartLoading] = useState(false);
    const [advertiserLoading, setAdvertiserLoading] = useState(false);

    useEffect(() => {
        doGetWatch();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        doGetAdvertiserAndItsReviews();
        doCountView();
        // eslint-disable-next-line
    }, [watch])

    useEffect(() => {
        if (!cartUpdated && userId) {
            getCartItems(userId);
        }
        // eslint-disable-next-line
    }, [cartUpdated])

    const doGetWatch = async () => {
        setLoading(true);
        if (!slug) {
            setLoading(false);
            return null;
        }
        const w = await getWatchBySlug(slug);
        if (!w) {
            setLoading(false);
            return null;
        }
        setWatch(w);
        setLoading(false);
    }

    const handleCart = async () => {
        if (!userId) {
            navigate("/gatekeeper");
            return null;
        }
        setCartLoading(true);
        await addToCart(userId, watch.id);
        setCartLoading(false);
    }

    const doRemoveFromCart = async () => {
        if (!userId) {
            return null;
        }
        setCartLoading(true);
        await removeFromCart(userId, watch.id);
        setCartLoading(false);
    }

    const doGetAdvertiserAndItsReviews = async () => {
        if (!watch) {
            return null;
        }
        const adv = isBlocked
            ? {}
            : await getAdvertiser(watch.advertiser);

        if (!adv) {
            return null;
        }
        setAdvertiser(adv);
        const revs = isBlocked
            ? []
            : await getPublicAdvertiserReviews(watch.advertiser);

        if (!revs) {
            return null;
        }
        setReviews(revs.filter(r => !r.hidden));
    }

    const handleAdvertiserModal = async () => {
        if (advertiser) {
            setAdvertiserModal(true);
            return null;
        }
        setAdvertiserLoading(true);
        setAdvertiserModal(true);
        const adv = isBlocked
            ? {}
            : await getAdvertiser(watch.advertiser);

        if (!adv) {
            setAdvertiserLoading(false);
            setAdvertiserModal(false);
            return null;
        }
        setAdvertiser(adv);
        setAdvertiserLoading(false);
    }

    const handleReviewPassage = (type: "prev" | "next") => {
        if (!reviews) {
            return null;
        }
        if (type === "next") {
            if (reviewIdx >= reviews.length - 1) {
                return null;
            }
            setReviewIdx(reviewIdx + 1);
        } else {
            if (reviewIdx <= 0) {
                return null;
            }
            setReviewIdx(reviewIdx - 1);
        }
    }

    const doCountView = async () => {
        if (!watch?.id) {
            return null;
        }
        const views = await getWatchViews(watch.id);
        if (!views) {
            return null;
        }
        await countWatchView(views.id);
    }

    if (loading) {
        return (
            <main className="h-screen">
                <div className="w-fit m-auto mt-10">
                    <LoadingBalls />
                </div>
            </main>
        )
    }

    return (
        <main className="max-w-7xl mx-auto px-4 md:px-10 py-24">
            <div className="flex flex-col lg:flex-row gap-16 items-start">
                <div className="lg:sticky lg:top-24 w-full lg:w-1/2">
                    {
                        isMobile ?
                            <MobileWatchImages images={watch?.images} />
                            :
                            <WatchImages images={watch?.images} />
                    }
                </div>

                <div className="flex-1 w-full space-y-8">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <p className="text-secondary font-bold tracking-[0.2em] text-xs uppercase">
                                {watch?.brand} | {watch?.year}
                            </p>
                            <VerifiedWatch />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-primary tracking-tight">
                            {watch?.model?.toUpperCase()}
                        </h1>
                        <p className="text-2xl font-light text-secondary">
                            R${watch?.price?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                    </div>

                    <div className="h-[1px] bg-surface w-full" />

                    <div className="space-y-4">
                        <h3 className="text-xs font-bold tracking-[0.2em] text-mono uppercase">Descrição</h3>
                        {
                            watch?.description ?
                                <p className="text-primary leading-relaxed text-lg whitespace-pre-line">{watch?.description}</p>
                                :
                                <p className="text-mono italic text-lg leading-relaxed">Nenhuma descrição fornecida para este item.</p>
                        }
                    </div>

                    <div className="h-[1px] bg-surface w-full" />

                    {
                        watch.status === "display" ?
                            <div className="flex flex-col gap-4 pt-4">
                                <button
                                    onClick={handleAdvertiserModal}
                                    className="w-full h-14 bg-primary text-white font-bold tracking-widest text-sm hover:bg-secondary transition-all duration-300 shadow-xl shadow-primary/10 active:scale-95 flex items-center justify-center gap-2"
                                >
                                    FALAR COM O ANUNCIANTE
                                </button>

                                {
                                    cart?.includes(watch.id) ?
                                        <div className="flex gap-4 justify-center items-center py-4 bg-surface rounded-xl">
                                            <p className="text-sm font-medium text-mono">Este item está na sua lista</p>
                                            {
                                                cartLoading ?
                                                    <LoadingBalls />
                                                    :
                                                    <button onClick={doRemoveFromCart} className="text-secondary font-bold text-sm hover:underline">Remover</button>
                                            }
                                        </div>
                                        :
                                        <button
                                            onClick={handleCart}
                                            className="w-full h-14 border-2 border-primary text-primary font-bold tracking-widest text-sm hover:bg-primary hover:text-white transition-all duration-300 active:scale-95 flex items-center justify-center"
                                        >
                                            {cartLoading ? <LoadingBalls /> : "ADICIONAR À MINHA LISTA"}
                                        </button>
                                }
                            </div>
                            :
                            <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 font-bold text-center uppercase tracking-widest text-sm">
                                Este item não está mais disponível
                            </div>
                    }

                    {/* Quick Specs Preview */}
                    <div className="grid grid-cols-2 gap-4 mt-10">
                        <div className="p-4 bg-moninho rounded-2xl">
                            <p className="text-[10px] text-mono font-bold tracking-widest uppercase mb-1">MOVIMENTO</p>
                            <p className="font-bold text-primary truncate">{movementFields[watch?.movement] || "-"}</p>
                        </div>
                        <div className="p-4 bg-moninho rounded-2xl">
                            <p className="text-[10px] text-mono font-bold tracking-widest uppercase mb-1">DIÂMETRO</p>
                            <p className="font-bold text-primary truncate">{watch?.diameter} mm</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-24">
                {
                    details ?
                        <div className="animate-superfade">
                            <button
                                onClick={() => setDetails(false)}
                                className="group flex items-center gap-2 mx-auto mb-10 text-mono hover:text-secondary transition-colors"
                            >
                                <span className="font-bold tracking-widest text-xs">ESCONDER DETALHES</span>
                                <div className="rotate-180 group-hover:-translate-y-1 transition-transform">
                                    <ChevronDownIcon fill="currentColor" width="20px" />
                                </div>
                            </button>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                {/* Technical Info */}
                                <div className="space-y-6">
                                    <h2 className="text-xl font-black text-primary tracking-widest uppercase flex items-center gap-3">
                                        <div className="w-8 h-1 bg-secondary" />
                                        Ficha Técnica
                                    </h2>

                                    <div className="bg-white rounded-3xl border border-surface overflow-hidden divide-y divide-surface shadow-sm">
                                        {[
                                            { label: "Marca", value: watch?.brand?.toUpperCase() },
                                            { label: "Modelo", value: watch?.model?.toUpperCase() },
                                            { label: "Ano", value: watch?.year },
                                            { label: "Estado", value: stateFields[watch?.state] },
                                            { label: "Movimento", value: movementFields[watch?.movement] },
                                            { label: "Modelo do Movimento", value: watch?.movementModel },
                                            { label: "Diametro", value: watch?.diameter },
                                            { label: "Funções", value: Array.isArray(watch?.functions) ? watch?.functions?.map((f: string) => functionsFields[f])?.filter((f: string) => f)?.join(", ") || "-" : "-"},
                                            { label: "Caixa e Docs", value: watch?.boxAndDocuments ? "Originais inclusos" : "Não informado" },
                                            { label: "Mat. da Caixa", value: caseMaterialFields[watch?.caseMaterial] },
                                            { label: "Estanqueidade", value: `${watch?.tightness} ATM` },
                                            { label: "Vidro", value: glassFields[watch?.glass] },
                                            { label: "Mat. do Bracelete", value: braceletMaterialFields[watch?.braceletMaterial] },
                                            { label: "Conteúdo", value: watch?.content }
                                        ].map((item, idx) => (
                                            <div key={idx} className="grid grid-cols-2 p-4 md:p-5 hover:bg-moninho transition-colors">
                                                <span className="text-xs font-bold text-mono uppercase tracking-widest">{item.label}</span>
                                                <span className="text-sm font-medium text-primary text-right">{item.value || "-"}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Advertiser Info */}
                                <div className="space-y-6">
                                    <h2 className="text-xl font-black text-primary tracking-widest uppercase flex items-center gap-3">
                                        <div className="w-8 h-1 bg-secondary" />
                                        O Anunciante
                                    </h2>

                                    <div className="relative group rounded-3xl overflow-hidden shadow-lg">
                                        {isBlocked ?
                                            <div className="absolute inset-0 bg-primary/10 backdrop-blur-md flex items-center justify-center z-10 transition-all duration-500 group-hover:backdrop-blur-lg">
                                                <div className="text-center p-8 bg-white/90 rounded-3xl shadow-2xl max-w-xs scale-90 md:scale-100">
                                                    <div className="mx-auto mb-4">
                                                        <UserIcon width="40px" fill="#111827" />
                                                    </div>
                                                    <p className="mb-6 font-bold text-primary leading-tight">
                                                        {!user ? "AUTENTIQUE-SE PARA VER O ANUNCIANTE" :
                                                            !user?.emailVerified ? "CONFIRME SEU EMAIL" :
                                                                "ACEITE OS TERMOS DE USO"}
                                                    </p>
                                                    <button
                                                        onClick={() => navigate(!user ? "/gatekeeper" : "/me", { state: { from: `/watches/${slug}` } })}
                                                        className="w-full bg-primary text-secondary font-black py-4 rounded-2xl hover:bg-primary/90 transition-all"
                                                    >
                                                        {!user ? "ENTRAR AGORA" : "PROSSEGUIR"}
                                                    </button>
                                                </div>
                                            </div>
                                            : null}

                                        <div className={`bg-white p-8 space-y-8 ${isBlocked ? "opacity-30 pointer-events-none select-none" : ""}`}>
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 bg-moninho rounded-full flex items-center justify-center text-2xl font-black text-primary border-2 border-secondary/20">
                                                    {advertiser?.name?.[0]?.toUpperCase()}
                                                </div>
                                                <div>
                                                    <h3 className="font-extrabold text-xl text-primary">{advertiser?.name}</h3>
                                                    {reviews && <TemplateStars width="20px" rate={calculateRate(reviews)} />}
                                                </div>
                                            </div>

                                            <div className="divide-y divide-surface border-t border-surface">
                                                <div className="py-4 flex justify-between gap-4">
                                                    <span className="text-xs font-bold text-mono tracking-widest uppercase">EMAIL</span>
                                                    <span className="text-sm font-medium text-primary break-all">{advertiser?.email}</span>
                                                </div>
                                                <div className="py-4 flex justify-between gap-4">
                                                    <span className="text-xs font-bold text-mono tracking-widest uppercase">TELEFONE</span>
                                                    <span className="text-sm font-medium text-primary">{formatPhoneNumber(advertiser?.phoneNumber)}</span>
                                                </div>
                                            </div>

                                            {reviews?.length ? (
                                                <div className="pt-6">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h4 className="text-[10px] font-black tracking-widest text-secondary uppercase">Avaliações ({reviews.length})</h4>
                                                        <div className="flex gap-2">
                                                            <button onClick={() => handleReviewPassage("prev")} disabled={reviewIdx <= 0} className="p-2 bg-moninho rounded-full disabled:opacity-30 transform rotate-90">
                                                                <ChevronDownIcon width="16px" />
                                                            </button>
                                                            <button onClick={() => handleReviewPassage("next")} disabled={reviewIdx >= reviews.length - 1} className="p-2 bg-moninho rounded-full disabled:opacity-30 transform -rotate-90">
                                                                <ChevronDownIcon width="16px" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="bg-moninho p-6 rounded-2xl transition-all h-[120px] overflow-y-auto">
                                                        <Review review={reviews[reviewIdx]} />
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        :
                        <button
                            onClick={() => setDetails(true)}
                            className="group flex flex-col items-center gap-3 mx-auto text-mono hover:text-secondary transition-all"
                        >
                            <span className="font-black tracking-[0.3em] text-xs">VER TODOS OS DETALHES</span>
                            <div className="p-3 bg-moninho rounded-full group-hover:bg-secondary group-hover:text-white transition-all shadow-sm">
                                <PlusIcon width="20px" fill="currentColor" />
                            </div>
                        </button>
                }
            </div>

            {
                advertiserModal ?
                    <AdvertiserModal adv={advertiser} loading={advertiserLoading} close={() => setAdvertiserModal(false)} slug={slug} />
                    : null
            }
        </main>
    )
}
